import * as React from "react";
import {useState, useEffect} from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {POST, GET} from "APIclient";
import properties from "constants/ApplicationConstants";

const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes toastIn {
    from { opacity: 0; transform: translateX(40px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .aal-page * { box-sizing: border-box; }
  .aal-page { font-family: 'DM Sans', 'Segoe UI', sans-serif; }

  /* MUI DatePicker override */
  .aal-datepicker .MuiInputBase-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.91rem !important;
    border-radius: 10px !important;
    background: #f8fafc !important;
    border: 1.5px solid #e2e8f0 !important;
    padding: 2px 8px !important;
  }
  .aal-datepicker .MuiOutlinedInput-notchedOutline { border: none !important; }
  .aal-datepicker .MuiInputBase-root:focus-within {
    border-color: #3b55e6 !important;
    box-shadow: 0 0 0 3px rgba(59,85,230,0.10) !important;
    background: #fff !important;
  }
  .aal-datepicker .MuiInputLabel-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.85rem !important;
  }
  .aal-datepicker .MuiFormHelperText-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.72rem !important;
  }

  .aal-balance-row:hover { background: #f0f4ff !important; }
  .aal-select:focus { outline: none; box-shadow: 0 0 0 3px rgba(59,85,230,0.10); }
  .aal-btn-primary:not(:disabled):hover { transform: translateY(-1px); }
  .aal-btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }

  /* ── Grid layout ── */
  .aal-grid {
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 12px;
    align-items: start;
  }
  @media (max-width: 767px) {
    .aal-grid { grid-template-columns: 1fr; gap: 10px; }
    .aal-note-card { order: 2; }
    .aal-main-card { order: 1; }
  }

  /* Page padding */
  @media (max-width: 479px) {
    .aal-page-wrap { padding: 8px 6px !important; }
  }

  /* Date row */
  .aal-date-row {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }
  .aal-date-wrap { flex: 1; min-width: 150px; }
  @media (max-width: 479px) {
    .aal-date-wrap { min-width: 100%; }
  }

  /* Card padding on phones */
  @media (max-width: 479px) {
    .aal-card-body { padding: 12px 14px 14px !important; }
    .aal-note-body { padding: 12px 14px !important; }
    .aal-header-inner { padding: 12px 16px 10px !important; }
    .aal-note-header-inner { padding: 10px 16px !important; }
  }

  /* Balance table on phones */
  @media (max-width: 479px) {
    .aal-balance-th { padding: 6px 8px !important; font-size: 0.65rem !important; }
    .aal-balance-td { padding: 6px 8px !important; font-size: 0.80rem !important; }
  }

  /* Employee + checkbox row: stack on phones */
  .aal-emp-row {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }
  .aal-emp-select-wrap { flex: 1; min-width: 200px; }
  @media (max-width: 479px) {
    .aal-emp-select-wrap { min-width: 100%; }
    .aal-checkbox-wrap { min-width: 100%; }
  }

  /* Toast */
  .aal-toast {
    position: fixed;
    top: 24px;
    right: 24px;
    z-index: 9999;
    padding: 14px 20px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.87rem;
    font-weight: 600;
    font-family: inherit;
    box-shadow: 0 8px 32px rgba(0,0,0,0.14);
    animation: toastIn 0.35s cubic-bezier(.22,1,.36,1) both;
    max-width: 340px;
  }
  @media (max-width: 479px) {
    .aal-toast {
      top: auto !important;
      bottom: 16px;
      right: 12px;
      left: 12px;
      max-width: 100%;
      font-size: 0.82rem;
    }
  }

  /* Custom checkbox */
  .aal-checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 0.82rem;
    font-weight: 600;
    color: #4a5568;
    user-select: none;
    white-space: nowrap;
  }
  .aal-checkbox-label input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: #3b55e6;
    cursor: pointer;
  }
`;

const s = {
  page: {
    padding: "10px",
    background: "linear-gradient(135deg, #f0f4ff 0%, #fafbff 60%, #eef2ff 100%)",
    minHeight: "100%",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif"
  },
  card: {
    borderRadius: 20,
    overflow: "hidden",
    boxShadow: "0 24px 64px rgba(67,97,238,0.10), 0 4px 16px rgba(0,0,0,0.06)",
    background: "#fff",
    animation: "slideUp 0.45s cubic-bezier(.22,1,.36,1) both"
  },
  header: {
    background: "linear-gradient(135deg, #3b55e6 0%, #5e75f5 100%)",
    position: "relative",
    overflow: "hidden"
  },
  hCircle1: {
    position: "absolute",
    width: 130,
    height: 130,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.08)",
    top: -40,
    right: -30,
    pointerEvents: "none"
  },
  hCircle2: {
    position: "absolute",
    width: 60,
    height: 60,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.06)",
    bottom: -15,
    left: 15,
    pointerEvents: "none"
  },
  headerIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    background: "rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 7,
    backdropFilter: "blur(4px)"
  },
  headerTitle: {
    color: "#fff",
    fontSize: "1.1rem",
    fontWeight: 700,
    margin: 0,
    letterSpacing: "-0.3px",
    fontFamily: properties.fontFamily
  },
  headerSub: {
    color: "rgba(255,255,255,0.72)",
    fontFamily: properties.fontFamily,
    fontSize: "0.76rem",
    marginTop: 2,
    fontWeight: 400
  },

  body: {padding: "14px 20px 16px"},

  balanceTable: {width: "100%", borderCollapse: "collapse", marginBottom: 4},
  balanceTh: {
    padding: "7px 12px",
    background: "#f8fafc",
    fontSize: "0.70rem",
    fontWeight: 700,
    color: "#718096",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "1.5px solid #e2e8f0",
    textAlign: "left"
  },
  balanceTd: {
    padding: "7px 12px",
    fontSize: "0.85rem",
    color: "#2d3748",
    borderBottom: "1px solid #f0f4f8",
    transition: "background 0.15s"
  },
  balanceBadge: val => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 36,
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: "0.78rem",
    fontWeight: 700,
    background: val > 0 ? "#ebf8ff" : "#fff5f5",
    color: val > 0 ? "#2b6cb0" : "#c53030",
    border: `1.5px solid ${val > 0 ? "#bee3f8" : "#feb2b2"}`
  }),

  divider: {height: 1, background: "#edf2f7", margin: "10px 0"},

  sectionLabel: {
    fontSize: "0.70rem",
    fontWeight: 700,
    color: "#718096",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 8
  },

  fieldGroup: {marginBottom: 10},
  label: {
    display: "block",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#4a5568",
    marginBottom: 5,
    letterSpacing: "0.4px",
    textTransform: "uppercase"
  },
  required: {color: "#e53e3e", marginLeft: 2},

  selectWrap: {position: "relative"},
  select: {
    width: "100%",
    padding: "8px 36px 8px 12px",
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    background: "#f8fafc",
    fontSize: "0.88rem",
    color: "#2d3748",
    outline: "none",
    fontFamily: "inherit",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    transition: "border-color 0.2s, box-shadow 0.2s"
  },
  selectError: {borderColor: "#fc8181", background: "#fff5f5"},
  selectArrow: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    color: "#a0aec0"
  },

  textarea: {
    width: "100%",
    padding: "8px 12px",
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    background: "#f8fafc",
    fontSize: "0.88rem",
    color: "#2d3748",
    outline: "none",
    fontFamily: "inherit",
    resize: "vertical",
    minHeight: 60,
    transition: "border-color 0.2s, box-shadow 0.2s"
  },
  textareaError: {borderColor: "#fc8181", background: "#fff5f5"},
  textareaFocus: {
    borderColor: "#3b55e6",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(59,85,230,0.10)"
  },

  errorMsg: {
    color: "#e53e3e",
    fontSize: "0.74rem",
    marginTop: 5,
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontWeight: 500
  },

  btnPrimary: {
    width: "100%",
    padding: "10px 28px",
    borderRadius: 10,
    border: "none",
    background: "linear-gradient(135deg, #3b55e6 0%, #5e75f5 100%)",
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.88rem",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "opacity 0.15s, transform 0.15s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    boxShadow: "0 4px 16px rgba(59,85,230,0.28)",
    marginTop: 4
  },

  autoApproveBadge: checked => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 12px",
    borderRadius: 20,
    fontSize: "0.78rem",
    fontWeight: 600,
    cursor: "pointer",
    userSelect: "none",
    transition: "all 0.2s",
    background: checked ? "#ebf8ff" : "#f8fafc",
    color: checked ? "#2b6cb0" : "#718096",
    border: `1.5px solid ${checked ? "#bee3f8" : "#e2e8f0"}`
  }),

  noteCard: {
    borderRadius: 20,
    overflow: "hidden",
    boxShadow: "0 8px 32px rgba(67,97,238,0.08)",
    background: "#fff",
    animation: "slideUp 0.55s cubic-bezier(.22,1,.36,1) both"
  },
  noteHeader: {
    background: "linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)",
    padding: "12px 18px",
    position: "relative",
    overflow: "hidden"
  },
  noteHCircle: {
    position: "absolute",
    width: 80,
    height: 80,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.12)",
    top: -25,
    right: -15,
    pointerEvents: "none"
  },
  noteHeaderTitle: {
    color: "#fff",
    fontFamily: properties.fontFamily,
    fontSize: "0.9rem",
    fontWeight: 700,
    margin: 0
  },
  noteBody: {padding: "14px 18px"},
  noteText: {fontSize: "0.82rem", color: "#4a5568", lineHeight: 1.6, margin: 0},
  noteHighlight: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "#fffbeb",
    border: "1px solid #fbd38d",
    borderRadius: 8,
    padding: "6px 10px",
    marginTop: 8,
    fontSize: "0.75rem",
    color: "#744210",
    fontWeight: 600
  },

  toastSuccess: {background: "#f0fff4", color: "#276749", border: "1.5px solid #9ae6b4"},
  toastError: {background: "#fff5f5", color: "#c53030", border: "1.5px solid #feb2b2"}
};

function FieldError({msg}) {
  if (!msg) return null;
  return (
    <div style={s.errorMsg}>
      <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
      {msg}
    </div>
  );
}

const SelectArrow = () => (
  <span style={s.selectArrow}>
    <svg
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  </span>
);

export default function AdminApplyLeave() {
  const [empId, setEmpId] = useState(Number(window.sessionStorage.getItem("empId")));

  const initialLeaveRequest = {
    startDate: dayjs(),
    endDate: dayjs(),
    leaveType: "",
    reason: "",
    employee: empId
  };

  const [leaveRequest, setLeaveRequest] = useState(initialLeaveRequest);
  const [leaveBalances, setLeaveBalances] = useState([
    {id: 1, LeaveType: "Planned Leave", LeavesBalance: 0},
    {id: 2, LeaveType: "Unplanned Leave", LeavesBalance: 0},
    {id: 3, LeaveType: "Compensatory Off", LeavesBalance: 0}
  ]);
  const [errors, setErrors] = useState({
    leaveType: "",
    reason: "",
    startDate: "",
    endDate: "",
    employee: ""
  });
  const [toast, setToast] = useState({open: false, color: "", message: ""});
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isAutoApproved, setIsAutoApproved] = useState(true);
  const [updateFlag, setUpdateFlag] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [reasonFocused, setReasonFocused] = useState(false);

  useEffect(() => {
    if (toast.open) {
      const t = setTimeout(() => setToast(p => ({...p, open: false})), 3000);
      return () => clearTimeout(t);
    }
  }, [toast.open]);

  useEffect(() => {
    if (!empId) return;
    const fetchLeavesData = async () => {
      try {
        const response = await GET(`employee/remaining-leaves/${empId}`);
        if (response.status === 200 && response.data) {
          const {plannedLeaves, unplannedLeaves, coffLeaves} = response.data.data;
          setLeaveBalances([
            {id: 1, LeaveType: "Planned Leave", LeavesBalance: plannedLeaves || 0},
            {id: 2, LeaveType: "Unplanned Leave", LeavesBalance: unplannedLeaves || 0},
            {id: 3, LeaveType: "Compensatory Off", LeavesBalance: coffLeaves || 0}
          ]);
        }
      } catch (error) {
        if (error.response) {
          setToast({
            open: true,
            color: "error",
            message: error.response.data?.message || "Error fetching leave data"
          });
        }
      }
    };
    fetchLeavesData();
  }, [empId, updateFlag]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await GET("admin/employeeDetails");
        if (response.status === 200 && Array.isArray(response.data.data)) {
          setEmployeeList(response.data.data);
        } else {
          setEmployeeList([]);
        }
      } catch (error) {
        setEmployeeList([]);
        if (error.response) {
          setToast({
            open: true,
            color: "error",
            message: error.response.data?.message || "Error fetching employees"
          });
        }
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const valid =
      leaveRequest.employee &&
      dayjs(leaveRequest.startDate).isValid() &&
      dayjs(leaveRequest.endDate).isValid() &&
      !dayjs(leaveRequest.endDate).isBefore(leaveRequest.startDate, "day") &&
      leaveRequest.leaveType &&
      leaveRequest.reason;
    setIsButtonDisabled(!valid);
  }, [leaveRequest]);

  const validateForm = () => {
    const e = {};
    if (!leaveRequest.employee) e.employee = "Employee selection is required";
    if (!leaveRequest.leaveType) e.leaveType = "Leave type is required";
    if (!leaveRequest.reason) e.reason = "Reason is required";
    if (!dayjs(leaveRequest.startDate).isValid()) e.startDate = "Valid start date is required";
    if (!dayjs(leaveRequest.endDate).isValid()) e.endDate = "Valid end date is required";
    if (dayjs(leaveRequest.endDate).isBefore(leaveRequest.startDate, "day"))
      e.endDate = "End date cannot be earlier than start date";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleEmployeeChange = e => {
    const {value} = e.target;
    setEmpId(value);
    setLeaveRequest(prev => ({...prev, employee: value}));
    setErrors(prev => ({...prev, employee: ""}));
  };

  const handleChange = e => {
    const {name, value} = e.target;
    setLeaveRequest(prev => ({...prev, [name]: value}));
    setErrors(prev => ({...prev, [name]: ""}));
  };

  const handleDateChange = (name, date) => {
    setLeaveRequest(prev => ({...prev, [name]: date}));
    const newErrors = {...errors, [name]: ""};
    if (!dayjs(date).isValid()) {
      newErrors[name] = `${name === "startDate" ? "Start" : "End"} date must be valid`;
    } else if (name === "endDate" && dayjs(date).isBefore(leaveRequest.startDate, "day")) {
      newErrors.endDate = "End date cannot be before start date";
    } else if (name === "startDate" && dayjs(leaveRequest.endDate).isBefore(date, "day")) {
      newErrors.endDate = "End date cannot be before start date";
    }
    setErrors(newErrors);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) {
      setToast({open: true, color: "error", message: "Please fill in all required fields."});
      return;
    }

    const payload = {
      startDate: dayjs(leaveRequest.startDate).format("YYYY-MM-DD"),
      endDate: dayjs(leaveRequest.endDate).format("YYYY-MM-DD"),
      leaveType: leaveRequest.leaveType,
      reason: leaveRequest.reason,
      employee: {empId: empId},
      leaveAction: isAutoApproved ? "Approved" : "Raise Request",
      appliedByAdmin: true
    };

    try {
      const response = await POST("employee/applyLeave", payload);
      if (response.status === 200) {
        setToast({open: true, color: "success", message: response.data.message});
        setLeaveRequest(initialLeaveRequest);
        setUpdateFlag(prev => !prev);
        setErrors({leaveType: "", reason: "", startDate: "", endDate: "", employee: ""});
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data || "An error occurred.";
      setToast({open: true, color: "error", message: msg});
    }
  };

  const leaveTypeOptions = [
    {value: "planned", label: "Planned Leave"},
    {value: "unplanned", label: "Unplanned Leave"},
    {value: "compensatory", label: "Compensatory Leave"},
    {value: "paternity", label: "Paternity Leave"},
    {value: "lwp", label: "Leave Without Pay (LWP)"},
    {value: "onsite", label: "Onsite Leave"}
  ];

  return (
    <DashboardLayout>
      <style>{globalCSS}</style>
      <DashboardNavbar />

      <div className="aal-page aal-page-wrap" style={s.page}>
        <div className="aal-grid">
          <div className="aal-main-card" style={s.card}>
            <div className="aal-header-inner" style={{...s.header, padding: "14px 22px 12px"}}>
              <div style={s.hCircle1} />
              <div style={s.hCircle2} />
              <div style={s.headerIcon}>
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.2"
                  viewBox="0 0 24 24"
                >
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 00-3-3.87" />
                  <path d="M16 3.13a4 4 0 010 7.75" />
                </svg>
              </div>
              <p style={s.headerTitle}>Admin — Apply Leave</p>
              <p style={s.headerSub}>Apply leave on behalf of an employee</p>
            </div>

            <div className="aal-card-body" style={s.body}>
              <p style={s.sectionLabel}>Employee Leave Balance</p>
              <table style={s.balanceTable}>
                <thead>
                  <tr>
                    <th className="aal-balance-th" style={s.balanceTh}>
                      #
                    </th>
                    <th className="aal-balance-th" style={s.balanceTh}>
                      Leave Type
                    </th>
                    <th className="aal-balance-th" style={{...s.balanceTh, textAlign: "center"}}>
                      Days Left
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leaveBalances.map(row => (
                    <tr
                      key={row.id}
                      className="aal-balance-row"
                      style={{transition: "background 0.15s"}}
                    >
                      <td
                        className="aal-balance-td"
                        style={{
                          ...s.balanceTd,
                          color: "#a0aec0",
                          fontSize: "0.78rem",
                          fontWeight: 600
                        }}
                      >
                        {row.id}
                      </td>
                      <td className="aal-balance-td" style={s.balanceTd}>
                        {row.LeaveType}
                      </td>
                      <td className="aal-balance-td" style={{...s.balanceTd, textAlign: "center"}}>
                        <span style={s.balanceBadge(row.LeavesBalance)}>{row.LeavesBalance}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={s.divider} />

              <p style={s.sectionLabel}>Select Employee</p>
              <div className="aal-emp-row">
                <div className="aal-emp-select-wrap">
                  <div style={s.fieldGroup}>
                    <div style={s.selectWrap}>
                      <select
                        name="employee"
                        value={leaveRequest.employee}
                        onChange={handleEmployeeChange}
                        className="aal-select"
                        style={{...s.select, ...(errors.employee ? s.selectError : {})}}
                      >
                        <option value="">Select employee</option>
                        {employeeList.length > 0 ? (
                          employeeList.map(emp => (
                            <option key={emp.empId} value={emp.empId}>
                              {emp.firstName} {emp.lastName} [{emp.empId}]
                            </option>
                          ))
                        ) : (
                          <option disabled>No employees available</option>
                        )}
                      </select>
                      <SelectArrow />
                    </div>
                    <FieldError msg={errors.employee} />
                  </div>
                </div>

                <div className="aal-checkbox-wrap">
                  <label style={s.autoApproveBadge(isAutoApproved)}>
                    <input
                      type="checkbox"
                      checked={isAutoApproved}
                      onChange={e => setIsAutoApproved(e.target.checked)}
                      style={{width: 15, height: 15, accentColor: "#3b55e6", cursor: "pointer"}}
                    />
                    {isAutoApproved ? (
                      <svg
                        width="13"
                        height="13"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        viewBox="0 0 24 24"
                      >
                        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    ) : (
                      <svg
                        width="13"
                        height="13"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        viewBox="0 0 24 24"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="8" y1="12" x2="16" y2="12" />
                      </svg>
                    )}
                    Auto Approve
                  </label>
                </div>
              </div>

              <div style={s.divider} />

              <p style={s.sectionLabel}>Select Dates</p>
              <div className="aal-date-row">
                <div className="aal-date-wrap">
                  <label style={s.label}>
                    Start Date<span style={s.required}>*</span>
                  </label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="aal-datepicker">
                      <DatePicker
                        format="DD/MM/YYYY"
                        value={leaveRequest.startDate}
                        onChange={date => handleDateChange("startDate", date)}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            error: !!errors.startDate,
                            helperText: errors.startDate,
                            sx: {
                              "& .MuiInputBase-root": {
                                borderRadius: "10px",
                                fontSize: "0.91rem",
                                fontFamily: "'DM Sans', sans-serif"
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </LocalizationProvider>
                </div>
                <div className="aal-date-wrap">
                  <label style={s.label}>
                    End Date<span style={s.required}>*</span>
                  </label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="aal-datepicker">
                      <DatePicker
                        format="DD/MM/YYYY"
                        value={leaveRequest.endDate}
                        onChange={date => handleDateChange("endDate", date)}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            error: !!errors.endDate,
                            helperText: errors.endDate,
                            sx: {
                              "& .MuiInputBase-root": {
                                borderRadius: "10px",
                                fontSize: "0.91rem",
                                fontFamily: "'DM Sans', sans-serif"
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </LocalizationProvider>
                </div>
              </div>

              <div style={s.fieldGroup}>
                <label style={s.label}>
                  Leave Type<span style={s.required}>*</span>
                </label>
                <div style={s.selectWrap}>
                  <select
                    name="leaveType"
                    value={leaveRequest.leaveType}
                    onChange={handleChange}
                    className="aal-select"
                    style={{...s.select, ...(errors.leaveType ? s.selectError : {})}}
                  >
                    <option value="">Select leave type</option>
                    {leaveTypeOptions.map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <SelectArrow />
                </div>
                <FieldError msg={errors.leaveType} />
              </div>

              <div style={s.fieldGroup}>
                <label style={s.label}>
                  Reason<span style={s.required}>*</span>
                </label>
                <textarea
                  name="reason"
                  value={leaveRequest.reason}
                  onChange={handleChange}
                  onFocus={() => setReasonFocused(true)}
                  onBlur={() => setReasonFocused(false)}
                  placeholder="Reason for leave..."
                  style={{
                    ...s.textarea,
                    ...(errors.reason ? s.textareaError : {}),
                    ...(reasonFocused ? s.textareaFocus : {})
                  }}
                />
                <FieldError msg={errors.reason} />
              </div>

              <button
                className="aal-btn-primary"
                style={s.btnPrimary}
                onClick={handleSubmit}
                disabled={isButtonDisabled}
              >
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                >
                  <path d="M22 2L11 13" />
                  <path d="M22 2L15 22 11 13 2 9l20-7z" />
                </svg>
                {isAutoApproved ? "Apply & Approve Leave" : "Submit Leave Request"}
              </button>
            </div>
          </div>

          <div className="aal-note-card" style={s.noteCard}>
            <div className="aal-note-header-inner" style={{...s.noteHeader, padding: "12px 18px"}}>
              <div style={s.noteHCircle} />
              <p style={s.noteHeaderTitle}>
                <span style={{marginRight: 8}}>📋</span>Important Note
              </p>
            </div>
            <div className="aal-note-body" style={s.noteBody}>
              <p style={s.noteText}>
                While applying leaves for the <strong>current year</strong>, the balance of the
                current year will be utilised.
              </p>
              <p style={{...s.noteText, marginTop: 10}}>
                In case you are applying leave for the <strong>next year</strong> or{" "}
                <strong>previous year</strong>, it will be deducted from the respective year&apos;s
                balance.
              </p>
              <div style={s.noteHighlight}>
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                PL &amp; UPL leaves do not carry forward
              </div>

              <div
                style={{
                  ...s.noteHighlight,
                  marginTop: 8,
                  background: "#f0fff4",
                  border: "1px solid #9ae6b4",
                  color: "#276749"
                }}
              >
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Auto Approve skips manager review
              </div>
            </div>
          </div>
        </div>
      </div>

      {toast.open && (
        <div
          className="aal-toast"
          style={toast.color === "success" ? s.toastSuccess : s.toastError}
        >
          {toast.color === "success" ? (
            <svg
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          ) : (
            <svg
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          )}
          {toast.message}
        </div>
      )}
    </DashboardLayout>
  );
}
